/**
 * dexScanGuidance — the "guidance brain" for the Dex live-scan PiP HUD.
 *
 * Turns the raw per-frame scan signals into the guidance the PiP should show:
 * where the gaps are, which way to scroll to reach them, live progress, a
 * "this dex is already complete" shortcut, and reason-based coaching instead of
 * a blanket "hold steady". It maps straight onto the native NexPip3D fields
 * (message / detail / arrow / captured / target / bubble) plus a small
 * bottom-right catch-flash id.
 *
 * PURE + DISPLAY-ONLY. This module reads no globals and returns no side effects;
 * like the scan telemetry, it must NEVER influence a gate / veto / apply
 * decision — it only decides what the HUD tells the user. `missingIds` is passed
 * IN (not computed here) so the gap source can evolve without touching this
 * logic, and so the whole thing stays trivially testable.
 */
import type { DexLivePipPhase } from "./nexPip";

/** Direction the trainer is scrolling, derived from the session's lead-id sign. */
export type ScrollDir = "down" | "up" | "none";

/** Native arrow glyph keys understood by NexPip3D's drawB9DirectionGlyph. */
export type GuidanceArrow = "scroll-down" | "back" | "hold" | "none";

export type GuidanceBubble = "green" | "yellow" | "orange" | "red";

/**
 * Why the scan is struggling this moment — drives contextual coaching copy.
 *
 * Both remaining reasons are TECHNIQUE: something the user can change about how
 * they are scrolling. `hud_too_big` was removed 2026-07-23 — see the note at its
 * old branch in computeDexScanGuidance.
 */
export type CoachingReason = "too_fast" | "blurry" | null;

export interface DexScanGuidanceInput {
  /** Human label for the active variant ("Shiny", "Lucky", …). */
  variantLabel: string;
  /** True once a confident variant is locked for the session. */
  variantResolved: boolean;
  /** Base phase from `pipPhaseFromTotals` — the pre-grid fallback copy. */
  basePhase: DexLivePipPhase;
  /** Distinct dex numbers read across the session so far. */
  numbersRead: number;
  /**
   * Ascending list of dex numbers still MISSING for the active variant, within
   * the scope being scanned. Empty ⇒ the dex is complete (nothing to find).
   */
  missingIds: number[];
  /** Count already caught for the active variant, same scope as `missingIds`. */
  caught: number;
  /**
   * Distinct dex cells the scanner has SEEN this session, caught or NOT. This —
   * not `caught` — is the scan's progress: the objective of a scan is to sweep
   * every cell, so coverage is what tells the trainer how far through they are.
   */
  scanned: number;
  /** Dex numbers visible in the current frame (for direction relative to gaps). */
  onScreenIds: number[];
  /** Scroll direction this moment. */
  scrollDir: ScrollDir;
  /** Reason-based coaching signal, or null when the scan is healthy. */
  coaching?: CoachingReason;
}

export interface DexScanGuidance {
  /** Primary HUD line. */
  message: string;
  /** Secondary HUD line (gap targets / progress / coaching reason). */
  detail?: string;
  /** Directional glyph. */
  arrow: GuidanceArrow;
  /** Progress numerator for the native `captured` field. */
  captured: number;
  /** Progress denominator for the native `target` field (>= 1). */
  target: number;
  /** Bubble colour. */
  bubble: GuidanceBubble;
  /** True when the active dex has no remaining gaps (complete). */
  complete: boolean;
}

/**
 * Compute the PiP guidance for the current frame. See module docs — pure and
 * display-only.
 */
export function computeDexScanGuidance(input: DexScanGuidanceInput): DexScanGuidance {
  const {
    variantLabel,
    variantResolved,
    basePhase,
    numbersRead,
    missingIds,
    caught,
    scanned,
    onScreenIds,
    scrollDir,
    coaching = null,
  } = input;

  const target = Math.max(1, caught + missingIds.length);

  // 1) Pre-grid: not locked onto a variant yet, or nothing read. Defer to the
  //    base phase copy so "open your Pokédex" / "start scrolling" still show.
  if (!variantResolved || numbersRead === 0) {
    return {
      message: BASE_MESSAGE[basePhase],
      arrow: "none",
      captured: scanned,
      target,
      bubble: BASE_BUBBLE[basePhase],
      complete: false,
    };
  }

  // 2) Complete dex — the reassuring shortcut. Nothing to hunt here.
  if (missingIds.length === 0) {
    return {
      message: `${variantLabel} complete ✓`,
      detail: "Nothing to scan here: switch dex",
      arrow: "none",
      captured: scanned,
      target,
      bubble: "green",
      complete: true,
    };
  }

  // 3) Contextual coaching wins over gap copy while it's firing — the user needs
  //    to fix HOW they're scanning before "next gap" copy is useful.
  //
  // REMOVED 2026-07-23: the "Shrink this window / It's covering the dex" nag.
  // Its trigger (a majority of the frame's cells obscured) fired constantly even
  // at the PiP's smallest size, so a red bubble flashed up through whole scans
  // and drowned the guidance that mattered. Asking the user to resize our own
  // window is the wrong ask anyway — if the HUD is in the way, that is ours to
  // fix, not theirs. The geometry guard is unaffected and still runs silently.
  if (coaching === "too_fast") {
    return {
      message: "Slow down",
      detail: "Scrolling past rows before they lock",
      arrow: "hold",
      captured: scanned,
      target,
      bubble: "orange",
      complete: false,
    };
  }
  if (coaching === "blurry") {
    return {
      message: "Hold steadier",
      detail: "Too blurry to read: ease the scroll",
      arrow: "hold",
      captured: scanned,
      target,
      bubble: "orange",
      complete: false,
    };
  }

  // 4) Gap targeting + directional driving. Point the trainer at the nearest
  //    gaps in the direction that reaches the most of them.
  const maxOnScreen = onScreenIds.length ? Math.max(...onScreenIds) : -Infinity;
  const minOnScreen = onScreenIds.length ? Math.min(...onScreenIds) : Infinity;
  const gapsBelow = missingIds.filter((id) => id > maxOnScreen); // reached by scrolling DOWN
  const gapsAbove = missingIds.filter((id) => id < minOnScreen); // behind — scroll UP

  let arrow: GuidanceArrow;
  if (gapsBelow.length > 0 && scrollDir !== "up") {
    // Gaps ahead in the natural scroll direction.
    arrow = "scroll-down";
  } else if (gapsBelow.length === 0 && gapsAbove.length > 0) {
    // Every remaining gap is behind — send them back up.
    arrow = "back";
  } else if (gapsBelow.length > 0) {
    // Scrolling up but gaps still exist below — nudge back down without shouting.
    arrow = "scroll-down";
  } else {
    // Gaps sit within the current view (not above or below) — hold and let them
    // lock rather than chasing a direction.
    arrow = "hold";
  }

  // TOP line = a STABLE instruction that only changes when the direction changes,
  // so the eye has something steady to read. The SECOND line names the dex being
  // scanned and nothing else.
  //
  // REMOVED 2026-07-23: "Looking for: #N #N". It named the numerically lowest
  // outstanding gaps, not the nearest ones — the "hold" branch took
  // missingIds.slice(0, 2) straight off the front of the dex — so a scan around
  // #650 sat on "#48" for its whole run. A target the user cannot act on from
  // where they are is worse than no target: it reads as the scan being lost.
  // Direction still drives the top line, and progress reaches the native pill
  // via captured/target. If per-gap targeting comes back it has to be relative
  // to what is ON SCREEN, not to the start of the dex.
  return {
    message: INSTRUCTION[arrow],
    detail: variantLabel,
    arrow,
    captured: scanned,
    target,
    bubble: "green",
    complete: false,
  };
}

/**
 * Stable top-line instruction per direction — deliberately short and steady.
 *
 * The `hold` / `none` resting copy is "Continue scanning slowly" (user's call
 * 2026-07-29): when the gaps sit in the current view there is no direction to
 * point, and the scan needs the trainer to keep easing through the dex to cover
 * the remaining cells. The old "Hold steady" here read as a contradiction the
 * moment the coaching layer flashed "Slow down", and was one of the messages the
 * HUD churned between.
 */
export const DEX_SCAN_RESTING_INSTRUCTION = "Continue scanning slowly";

/**
 * Hard cap on how many CONSECUTIVE frames the low-confidence "blurry" coaching
 * may fire for before it is force-suppressed. Mirrors
 * DEFAULT_HOLD_STEADY_CONFIG.maxShownFrames, which already bounds the sibling
 * "Slow down" prompt for the same reason: a wedged signal must never own the HUD
 * for the rest of the session.
 */
export const MAX_BLURRY_COACHING_FRAMES = 6;

/**
 * Frames the low-confidence condition must PERSIST before the "blurry"
 * coaching shows at all (2026-08-31 device report: the line fired on the
 * very first weak frame and owned the HUD through Perfect/Purified scans,
 * whose mostly-empty grids read few numbers per frame anyway). Mirrors the
 * sibling hold-steady prompt's persistFrames discipline: one bad frame is
 * noise, three in a row is a real blur episode. Together with the lowered
 * cap above, the line now shows for at most ~4 frames per episode — a
 * nudge, not a state.
 */
export const MIN_BLURRY_COACHING_FRAMES = 3;

export interface CoachingDecision {
  coaching: CoachingReason;
  /** Consecutive blurry-eligible frames so far — feed back in next frame. */
  blurryFrames: number;
}

/**
 * Decide this frame's coaching reason. Pure; the caller carries `blurryFrames`
 * across frames.
 *
 * Low confidence has two very different causes with opposite fixes. MOVING:
 * motion blur — "Hold steadier / ease the scroll" is right. STATIC (the dedup is
 * re-reading a held-still view): the cells are as resolved as this viewpoint will
 * ever make them; re-reading the same frame cannot clear an ambiguous cell, only
 * a fresh view can. So while stationary we DROP the "blurry" coaching and let the
 * caller fall through to gap-targeting scroll guidance, telling the user to move
 * on instead of the counterproductive "Hold steadier".
 *
 * 2026-08-10 — THE CAP. `admitWasStatic` is the only staticness signal here and
 * it reads a DEDUP verdict: only a byte-identical re-admit counts as static. A
 * held-still screen whose capture is NOT byte-identical (Android's live capture
 * re-encodes every frame; iOS sparkle/animation) therefore looks "moving" to this
 * branch, so a stuck low-confidence run pinned "Hold steadier / ease the scroll"
 * on a stationary screen with nothing to expire it. Past the cap the coaching goes
 * quiet and stays quiet until a frame reads confidently again — at which point the
 * counter clears and a genuine later blur episode coaches normally.
 */
export function decideCoaching(args: {
  /** True when the frame's PiP phase is `low_confidence`. */
  lowConfidence: boolean;
  /** True when the frame was re-admitted from a byte-identical (static) view. */
  admitWasStatic: boolean;
  /** True while the debounced hold-steady prompt is showing. */
  holdSteadyShown: boolean;
  /** Consecutive blurry-eligible frames before this one. */
  blurryFrames: number;
  /** Override the cap (tests). */
  maxBlurryFrames?: number;
  /** Override the persistence floor (tests). */
  minBlurryFrames?: number;
}): CoachingDecision {
  const cap = args.maxBlurryFrames ?? MAX_BLURRY_COACHING_FRAMES;
  const floor = args.minBlurryFrames ?? MIN_BLURRY_COACHING_FRAMES;
  if (args.lowConfidence && !args.admitWasStatic) {
    const blurryFrames = args.blurryFrames + 1;
    return {
      coaching: blurryFrames >= floor && blurryFrames <= cap ? "blurry" : null,
      blurryFrames,
    };
  }
  // Not a blur candidate: the streak is over. A low-confidence STATIC frame
  // coaches nothing (re-reading it cannot help); otherwise the hold-steady
  // detector's own debounced "Slow down" carries.
  return {
    coaching: args.lowConfidence ? null : args.holdSteadyShown ? "too_fast" : null,
    blurryFrames: 0,
  };
}

const INSTRUCTION: Record<GuidanceArrow, string> = {
  "scroll-down": "Scroll down to the next rows",
  back: "Scroll back up",
  hold: DEX_SCAN_RESTING_INSTRUCTION,
  none: DEX_SCAN_RESTING_INSTRUCTION,
};

// Base-phase copy for the pre-grid states. Mirrors DEX_PIP_COPY in nexPip.ts but
// kept local so this pure module has no dependency on that render table.
const BASE_MESSAGE: Record<DexLivePipPhase, string> = {
  idle: "Open a Pokédex or Pokémon screen",
  open_pokedex: "Open a Pokédex or Pokémon screen",
  scanning: "Show a Pokédex or Pokémon screen",
  start_scrolling: "Start scrolling slowly to scan",
  hold_steady: "Hold steady while entries confirm",
  scroll_slower: "Scroll slower",
  reading_grid: "Scanning…",
  low_confidence: "Hold steadier",
  saved: "Dex updated",
};

/**
 * Every grid-scan line the HUD can be showing on the frame a Pokémon page
 * first appears, for myPokemonOcr's HUD-echo filter (GO prints none of them).
 */
export const DEX_SCAN_HUD_LINES: readonly string[] = [
  ...Object.values(BASE_MESSAGE),
  ...Object.values(INSTRUCTION),
];

const BASE_BUBBLE: Record<DexLivePipPhase, GuidanceBubble> = {
  idle: "yellow",
  open_pokedex: "yellow",
  scanning: "yellow",
  start_scrolling: "yellow",
  hold_steady: "yellow",
  scroll_slower: "orange",
  reading_grid: "green",
  low_confidence: "orange",
  saved: "green",
};
